import { useCallback, useEffect, useMemo, useState } from "react";
import { Image as ImageIcon, RefreshCw, X } from "lucide-react";
import api from "../../lib/api";
import useAdminAutoRefresh from "../../lib/useAdminAutoRefresh";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "deposit", label: "Deposit" },
  { key: "withdraw", label: "Withdraw" },
  { key: "credit_claim", label: "Credit Claim" },
];

const TYPE_LABELS = {
  admin_credit: "Admin Credit",
  admin_debit: "Admin Debit",
  bonus_credit: "Bonus Credit",
  create_account: "Create Account",
  free_credit: "Free Credit",
  game_load: "Load",
  game_redeem: "Redeem",
  load: "Load",
  phone_claim_load: "Phone Claim_load",
  redeem: "Withdraw",
  referral_bonus: "Referral Bonus",
  reset_password: "Password Reset",
  wallet_transfer_received: "Transfer Received",
  wallet_transfer_sent: "Transfer Sent",
};

const HIDDEN_TRANSACTION_TYPES = new Set(["create_account"]);

export default function AdminTransactions() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [receiptPreview, setReceiptPreview] = useState(null);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const { data } = await api.get("/admin/transactions");
      setRows(Array.isArray(data) ? data : []);
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);
  useAdminAutoRefresh(() => load({ quiet: true }));

  const filteredRows = useMemo(
    () => rows.filter((row) => matchesFilter(row, filter)),
    [filter, rows]
  );

  return (
    <div className="p-3 sm:p-6 md:p-8">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight text-white">
          Transactions
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          {FILTERS.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setFilter(item.key)}
              className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${
                filter === item.key
                  ? "border-[#f5b014] bg-[#f5b014] text-black"
                  : "border-white/10 bg-[#0b0b0d] text-white hover:border-[#f5b014]/60"
              }`}
            >
              {item.label}
            </button>
          ))}
          <button
            type="button"
            onClick={load}
            className="rounded-full border border-white/10 bg-[#0b0b0d] p-2.5 text-slate-300 hover:border-[#f5b014]/60 hover:text-white"
            aria-label="Refresh transactions"
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      <TransactionsTable rows={filteredRows} onPreviewReceipt={setReceiptPreview} />

      {receiptPreview && (
        <ReceiptPreview receipt={receiptPreview} onClose={() => setReceiptPreview(null)} />
      )}
    </div>
  );
}

export function TransactionsTable({
  rows,
  empty = "No transactions found.",
  onPreviewReceipt,
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-[#332a10] bg-[#111111]">
      <table className="w-full min-w-[1120px] table-fixed text-sm">
        <thead className="bg-[#1f1f22] text-left text-xs uppercase tracking-wider text-slate-400">
          <tr>
            <th className="w-[14%] px-4 py-3">When</th>
            <th className="w-[15%] px-4 py-3">Type</th>
            <th className="w-[22%] px-4 py-3">Member</th>
            <th className="w-[15%] px-4 py-3">Game / Method</th>
            <th className="w-[8%] px-4 py-3 text-right">Amount</th>
            <th className="w-[6%] px-4 py-3">Receipt</th>
            <th className="w-[20%] px-4 py-3">Note</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const receipt = getReceiptUrl(row);
            return (
              <tr
                key={row.id || `${row.created_at || "row"}-${index}`}
                className="border-t border-[#332a10]/70 hover:bg-white/[0.025]"
              >
                <td className="px-4 py-3 text-slate-400">
                  {formatDateTime(row.created_at)}
                </td>
                <td className="px-4 py-3">
                  <TypePill>{getTypeLabel(row)}</TypePill>
                </td>
                <td className="px-4 py-3">
                  <div className="truncate font-semibold text-white">
                    {getMemberName(row)}
                  </div>
                  {row.email && (
                    <div className="truncate text-xs text-slate-500">{row.email}</div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="truncate font-medium text-white">
                    {getGameOrMethod(row)}
                  </div>
                </td>
                <td className="px-4 py-3 text-right font-semibold tabular-nums text-[#f5c400]">
                  <TransactionAmount row={row} />
                </td>
                <td className="px-4 py-3">
                  {receipt ? (
                    <button
                      type="button"
                      onClick={() => onPreviewReceipt?.({ row, url: receipt })}
                      className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-md border border-white/10 bg-white/[0.03] text-slate-300 hover:border-[#f5b014]/60 hover:text-white"
                      title="View receipt"
                    >
                      <ReceiptThumb url={receipt} />
                    </button>
                  ) : (
                    <span className="text-slate-500">-</span>
                  )}
                </td>
                <td className="px-4 py-3 text-slate-400">
                  <div className="line-clamp-2" title={getNote(row)}>
                    {getNote(row)}
                  </div>
                </td>
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr>
              <td colSpan={7} className="py-10 text-center text-slate-500">
                {empty}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export function StatusPill({ status }) {
  const tones = {
    pending: "bg-slate-800 border-white/10 text-slate-300",
    approved: "bg-emerald-900/30 border-emerald-700/40 text-emerald-300",
    rejected: "bg-red-900/30 border-red-700/40 text-red-300",
  };
  return (
    <span className={`rounded-md border px-2 py-0.5 text-xs ${tones[status] || tones.pending}`}>
      {status || "-"}
    </span>
  );
}

export function isWalletMoneyRequest(row) {
  if (!row) return false;
  if (["game_load", "game_redeem", "wallet_transfer"].includes(row.transfer_kind) || row.wallet_debit_amount || row.wallet_credit_amount) return true;
  if (row.payment_provider || row.payment_platform || row.payment_method) return true;
  if (["admin_credit", "admin_debit", "bonus_credit", "free_credit", "referral_bonus", "phone_claim_load", "wallet_transfer_received", "wallet_transfer_sent"].includes(row.type)) return true;
  const note = String(row.note || row.admin_note || "").toLowerCase();
  if (note.includes("$") || note.includes("add money") || note.includes("withdrawal") || note.includes("checkout") || note.includes("game load") || note.includes("bonus")) return true;
  return ["load", "redeem"].includes(row.type) && !row.game_id;
}

export function formatMoney(coins = 0) {
  return `$${((Number(coins) || 0) / 1000).toFixed(2)}`;
}

export function formatRequestAmount(row, empty = "-") {
  const amount = Number(row?.amount || row?.wallet_amount || 0);
  if (!amount) return empty;
  return formatMoney(amount);
}

function TransactionAmount({ row }) {
  if (row?.transfer_kind === "game_redeem" && (row?.freeplay_redeem_rule || row?.phone_verification_freeplay_rule)) {
    const provider = Number(row.provider_amount || 0) * 1000;
    const feePercent = Number(row.redeem_fee_percent || 50);
    const wallet = Number(row.wallet_credit_amount || row.amount || 0);
    return (
      <div>
        <div>{formatMoney(wallet)} net</div>
        <div className="mt-0.5 text-[11px] font-medium text-slate-500">
          Provider {formatMoney(provider)} - {feePercent}% fee
        </div>
      </div>
    );
  }
  return formatRequestAmount(row);
}

function TypePill({ children }) {
  return (
    <span className="inline-flex max-w-full items-center rounded-full border border-[#5b4a17] px-3 py-1 text-xs font-semibold text-white">
      <span className="truncate">{children}</span>
    </span>
  );
}

function ReceiptThumb({ url }) {
  const [broken, setBroken] = useState(false);
  if (broken) {
    return <ImageIcon className="h-4 w-4" />;
  }

  return (
    <img
      src={url}
      alt=""
      className="h-full w-full object-cover"
      onError={() => setBroken(true)}
    />
  );
}

function ReceiptPreview({ receipt, onClose }) {
  const row = receipt.row || {};
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/75 p-4">
      <div className="w-full max-w-2xl rounded-xl border border-[#5b4a17] bg-[#101411] p-4 shadow-2xl">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-white">Payment receipt</h2>
            <p className="text-sm text-slate-400">
              {getMemberName(row)} - {formatRequestAmount(row)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-white/10 p-2 text-slate-300 hover:text-white"
            aria-label="Close receipt"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="max-h-[70vh] overflow-auto rounded-lg bg-black/40 p-2">
          <img
            src={receipt.url}
            alt="Payment receipt"
            className="mx-auto max-h-[66vh] max-w-full rounded-md object-contain"
          />
        </div>
      </div>
    </div>
  );
}

function getTypeLabel(row) {
  const type = String(row?.type || "").toLowerCase();
  return TYPE_LABELS[type] || titleize(type || "-");
}

function getMemberName(row) {
  return row?.username || row?.user_name || row?.member || row?.email || "-";
}

function getGameOrMethod(row) {
  return (
    row?.game_display_name ||
    row?.game_name ||
    row?.gameName ||
    row?.game_id ||
    row?.gameId ||
    row?.payment_platform ||
    row?.payment_provider ||
    row?.payment_method ||
    row?.method ||
    "-"
  );
}

function getReceiptUrl(row) {
  return (
    row?.proof_image_url ||
    row?.receipt_url ||
    row?.receiptImageUrl ||
    row?.screenshot_url ||
    row?.payment_screenshot_url ||
    row?.image_url ||
    row?.receipt ||
    ""
  );
}

function getNote(row) {
  if (row?.transfer_kind === "game_redeem" && (row?.freeplay_redeem_rule || row?.phone_verification_freeplay_rule)) {
    const provider = Number(row.provider_amount || 0) * 1000;
    const feePercent = Number(row.redeem_fee_percent || 50);
    const fee = Number(row.redeem_fee_amount_units || 0) || Math.round(provider * (feePercent / 100));
    const wallet = Number(row.wallet_credit_amount || row.amount || 0);
    return `Freeplay redeem rule: provider redeem ${formatMoney(provider)}, ${feePercent}% fee ${formatMoney(fee)}, wallet credit ${formatMoney(wallet)}.`;
  }
  return row?.note || row?.admin_note || row?.remark || "-";
}

function matchesFilter(row, filter) {
  if (HIDDEN_TRANSACTION_TYPES.has(String(row?.type || "").toLowerCase())) return false;
  if (filter === "all") return true;
  const type = String(row?.type || "").toLowerCase();
  const note = String(getNote(row)).toLowerCase();
  const method = String(getGameOrMethod(row)).toLowerCase();

  if (filter === "deposit") {
    return (
      method.includes("paypal") ||
      method.includes("chime") ||
      method.includes("cash") ||
      method.includes("card") ||
      method.includes("apple") ||
      method.includes("google") ||
      method.includes("wallet_transfer") ||
      type === "wallet_transfer_received" ||
      Boolean(row?.payment_platform || row?.payment_provider || row?.payment_method) ||
      note.includes("add money") ||
      note.includes("deposit")
    );
  }

  if (filter === "withdraw") {
    return (
      ["redeem", "game_redeem", "admin_debit", "wallet_transfer_sent"].includes(type) ||
      note.includes("withdraw") ||
      note.includes("cash out") ||
      note.includes("redeem")
    );
  }

  if (filter === "credit_claim") {
    return (
      ["admin_credit", "bonus_credit", "free_credit", "referral_bonus", "phone_claim_load"].includes(type) ||
      note.includes("phone-verification") ||
      note.includes("free credit") ||
      note.includes("bonus") ||
      note.includes("referral")
    );
  }

  return true;
}

function titleize(value) {
  return String(value || "-")
    .replace(/_/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
}
